<template>
  <app-page title="Recognition" subtitle="Create, manage and report on your awards programme." icon="gift">
    <template #extraTitle>
      <active-company-pill class="ml-2" />
    </template>

    <!-- Render during loading too (as a spinner) so the stat reserves its height
         from first paint — otherwise it pops in afterwards and, via the header
         row's align-items-end, shoves the title downward. -->
    <template
      v-slot:header-content
      v-if="!isReadOnly && (isLoading || (recognitionPrograms && recognitionPrograms.length > 0))"
    >
      <app-stat
        :stat="isLoading ? undefined : recognitionLedger?.balance_minor ?? 0"
        :formatter="getCurrencyFormatter(recognitionLedger?.balance_minor ?? 0)"
        title="Balance"
        color="default"
        small
        class="header-stat"
      >
        <template #title-action>
          <router-link :to="{ name: 'recognition-settings' }" class="add-funds-link">
            <b-button variant="link" size="sm" class="p-0">
              <font-awesome-icon :icon="['fal', 'plus']" class="mr-1" />
              Add funds
            </b-button>
          </router-link>
        </template>
      </app-stat>
    </template>

    <template v-slot:after-header v-if="!isReadOnly && recognitionPrograms && recognitionPrograms.length > 0">
      <b-row no-gutters class="justify-content-end mt-4">
        <b-button variant="outline-primary" v-b-modal.give-recognition-modal class="mr-3"> Give recognition </b-button>
        <b-button variant="primary" v-b-modal.create-award-modal>
          <font-awesome-icon :icon="['fal', 'plus']" class="mr-2" />
          Create award
        </b-button>
      </b-row>
    </template>

    <template v-slot:custom-content>
      <template v-if="!isLoading">
        <!-- Empty state: show intro card when there are no awards/programs -->
        <b-col v-if="!recognitionPrograms || recognitionPrograms.length === 0">
          <b-card no-body>
            <b-row no-gutters>
              <b-col md="6">
                <b-card-body class="p-6 card-body d-flex flex-column justify-content-between">
                  <div>
                    <h3 class="font-weight-bold mb-3">Launch your first reward</h3>
                    <p>
                      Turn great work into moments worth celebrating, build a quick award and send it when someone
                      shines.
                    </p>
                  </div>

                  <b-button
                    v-if="!isReadOnly"
                    class="card-button"
                    variant="outline-secondary"
                    v-b-modal.create-award-modal
                  >
                    <font-awesome-icon :icon="['fal', 'plus']" class="mr-2" /><strong>Create award</strong>
                  </b-button>
                </b-card-body>
              </b-col>

              <b-col md="6" class="d-flex justify-content-end">
                <b-card-img :src="recognitionImg" alt="Image" class="recognition-image"></b-card-img>
              </b-col>
            </b-row>
          </b-card>
        </b-col>

        <!-- Tabbed interface when programs exist -->
        <b-col v-else>
          <sme-alert v-if="!isReadOnly && shortfallMinor > 0" level="warning" class="mb-3">
            <div class="shortfall-alert">
              <span>
                You're <strong>{{ formatShortfall(shortfallMinor) }}</strong> short of the
                <strong>{{ formatShortfall(totalUpcomingCostMinor) }}</strong> scheduled {{ upcomingWindowPhrase }}. Add
                <strong>{{ formatShortfall(shortfallMinor) }}</strong> to cover upcoming rewards.
              </span>
              <router-link :to="{ name: 'recognition-settings' }">Add funds</router-link>
            </div>
          </sme-alert>
          <sme-card large>
            <navigable-tabs content-class="mt-3">
              <template #tabs-end>
                <div class="tabs-end-actions">
                  <b-form-checkbox
                    v-if="activeTab === 'awards'"
                    v-model="showDisabledAwards"
                    switch
                    size="sm"
                    class="show-disabled-toggle"
                  >
                    Show disabled awards
                  </b-form-checkbox>
                  <b-form-checkbox
                    v-if="activeTab === 'activity'"
                    v-model="showHiddenActivity"
                    switch
                    size="sm"
                    class="show-disabled-toggle"
                  >
                    Show hidden activity
                  </b-form-checkbox>
                  <span v-if="!isReadOnly && activeTab === 'upcoming' && upcomingSummary" class="upcoming-summary">
                    <strong>{{ upcomingSummary.total }}</strong> due {{ upcomingSummary.windowText }}
                  </span>
                </div>
              </template>
              <b-tab id="awards">
                <template #title>
                  <span>Awards</span>
                </template>
                <div class="awards-filters">
                  <b-form-input
                    v-model="awardSearch"
                    type="search"
                    placeholder="Search awards"
                    class="awards-filters__filter"
                  />
                  <select-dropdown
                    v-if="hasScopeOptions"
                    v-model="selectedAwardScope"
                    :options="scopeOptions"
                    class="awards-filters__scope"
                  />
                </div>
                <awards-table
                  :programs="filteredPrograms"
                  :loading="isLoadingPrograms"
                  :pending-nominations="pendingNominations"
                  :toggling-program-id="togglingProgramId"
                  :toggling-group-id="togglingGroupId"
                  :show-disabled-awards="showDisabledAwards"
                  :view-only="isReadOnly"
                  @toggle-active="onToggleActive"
                  @toggle-group-active="onToggleGroupActive"
                />
              </b-tab>

              <b-tab id="budgets" v-if="budgetsEnabled">
                <template #title>
                  <span>Budgets</span>
                </template>
                <budgets-tab
                  :programs="recognitionPrograms"
                  :read-only="isReadOnly"
                  @changed="getRecognitionPrograms"
                />
              </b-tab>

              <b-tab id="pending-approval" v-if="!isReadOnly" @shown="onPendingTabShown">
                <template #title>
                  <span>
                    {{ budgetsEnabled ? 'Pending' : 'Pending approval' }}
                    <b-badge v-if="pendingNominations.length > 0" pill variant="primary" class="ml-1">
                      {{ pendingNominations.length }}
                    </b-badge>
                  </span>
                </template>
                <pending-approvals-table
                  :nominations="pendingNominations"
                  :loading="isLoadingPendingNominations"
                  @review="onReviewNomination"
                />
              </b-tab>

              <b-tab id="upcoming" v-if="!isReadOnly" @shown="onUpcomingTabShown">
                <template #title>
                  <span>
                    Upcoming
                    <b-badge v-if="scheduledUpcomingAwards.length > 0" pill variant="primary" class="ml-1">
                      {{ scheduledUpcomingAwards.length }}
                    </b-badge>
                  </span>
                </template>
                <upcoming-awards-table
                  :awards="upcomingAwards"
                  :programs="recognitionPrograms"
                  :loading="isLoadingUpcomingAwards"
                  @change="onUpcomingWindowChange"
                  @update:scope="onUpcomingScopeChange"
                  @update:summary="upcomingSummary = $event"
                  @update:window="upcomingWindow = $event"
                  @withdrawal-changed="getUpcomingAwards"
                />
              </b-tab>

              <b-tab id="reports" v-if="!isReadOnly" @shown="onReportsTabShown">
                <template #title>
                  <span>
                    {{ budgetsEnabled ? 'Reported' : 'Reported content' }}
                    <b-badge v-if="pendingReportsCount > 0" pill variant="danger" class="ml-1">
                      {{ pendingReportsCount }}
                    </b-badge>
                  </span>
                </template>
                <reports-table
                  :reports="contentReports"
                  :loading="isLoadingContentReports"
                  :truncated="contentReportsTruncated"
                  :pending-total="pendingReportsCount"
                  @review="onReviewReport"
                />
              </b-tab>

              <b-tab id="analytics">
                <template #title>
                  <span>Analytics</span>
                </template>
                <analytics-tab :programs="recognitionPrograms" />
              </b-tab>

              <b-tab id="activity">
                <template #title>
                  <span>Activity</span>
                </template>
                <activity-table
                  :programs="recognitionPrograms"
                  :show-hidden="showHiddenActivity"
                  :readonly="isReadOnly"
                  @withdrawn="onGrantWithdrawn"
                />
              </b-tab>

              <b-tab id="transactions" v-if="!isReadOnly">
                <template #title>
                  <span>Transactions</span>
                </template>
                <transactions-table ref="transactionsTableRef" :programs="recognitionPrograms" />
              </b-tab>
            </navigable-tabs>
          </sme-card>
        </b-col>
      </template>
    </template>

    <template v-slot:footer v-if="!isReadOnly">
      <create-award-modal @created="onProgramCreated" />
      <give-recognition-modal :programs="recognitionPrograms" @awarded="onAwardGiven" />
      <review-nomination-modal
        ref="reviewModalRef"
        :nomination="reviewingNomination"
        :programs="recognitionPrograms"
        @approved="onNominationReviewed"
        @rejected="onNominationReviewed"
      />
      <review-report-modal ref="reviewReportModalRef" :report="reviewingReport" @actioned="onReportActioned" />
      <confirm-disable-award-modal
        ref="confirmDisableModalRef"
        :program-name="togglingProgramName"
        :group-size="pendingGroupDisable ? pendingGroupDisable.programIds.length : 0"
        @confirmed="onDisableConfirmed"
      />
    </template>
  </app-page>
</template>

<script setup>
import { ref, computed, onMounted, watch } from 'vue';
import AppPage from '@/components/AppPage.vue';
import AppStat from '@/components/AppStat.vue';
import NavigableTabs from '@/components/NavigableTabs.vue';
import SelectDropdown from '@/components/SelectDropdown.vue';
import SmeCard from '@/components/atoms/SmeCard.vue';
import ActiveCompanyPill from '@/pages/recognition/components/ActiveCompanyPill.vue';
import CreateAwardModal from '@/pages/recognition/components/CreateAwardModal.vue';
import GiveRecognitionModal from '@/pages/recognition/components/GiveRecognitionModal.vue';
import ReviewNominationModal from '@/pages/recognition/components/ReviewNominationModal.vue';
import ReviewReportModal from '@/pages/recognition/components/ReviewReportModal.vue';
import ConfirmDisableAwardModal from '@/pages/recognition/components/ConfirmDisableAwardModal.vue';
import AwardsTable from '@/pages/recognition/components/AwardsTable.vue';
import PendingApprovalsTable from '@/pages/recognition/components/PendingApprovalsTable.vue';
import ReportsTable from '@/pages/recognition/components/ReportsTable.vue';
import UpcomingAwardsTable from '@/pages/recognition/components/UpcomingAwardsTable.vue';
import TransactionsTable from '@/pages/recognition/components/TransactionsTable.vue';
import ActivityTable from '@/pages/recognition/components/ActivityTable.vue';
import AnalyticsTab from '@/pages/recognition/components/AnalyticsTab.vue';
import BudgetsTab from '@/pages/recognition/components/budgets/BudgetsTab.vue';
import { getCurrencyFormatter, toCurrency } from '@/utils';
import ApiClient, { apiErrorData } from '@/ApiClient';
import { useQueryClient } from '@tanstack/vue-query';
import { getRecognitionContentReports, getRecognitionContentReportsCount } from '@/queries/recognition';
import { UPCOMING_AWARDS_DEFAULT_RANGE, getUpcomingWindowPhrase, programScope } from '@/pages/recognition/constants';
import useScopeFilter, { matchesScopeFilter } from '@/pages/recognition/composables/useScopeFilter';
import State from '@/state/State';
import SmeAlert from '@/components/atoms/SmeAlert.vue';
import useToast from '@/composables/useToast';
import useFeatureFlags from '@/composables/useFeatureFlags';
import recognitionImg from '@/assets/recognition-jigsaw-pieces.png';
import { useRoute } from 'vue-router/composables';
import { isRecognitionViewerClaims } from '@/utils/scopedAdminGate';

const { doToast, TOAST_VARIANT } = useToast();
const { recognitionBudgetsVersion } = useFeatureFlags();
const budgetsEnabled = computed(() => recognitionBudgetsVersion.value === 'v1');

const queryClient = useQueryClient();

const $route = useRoute();
// "Show disabled awards" only affects the Awards tab; hide it elsewhere.
const activeTab = computed(() => $route.query.tab || 'awards');

const reviewModalRef = ref(null);
const confirmDisableModalRef = ref(null);
const transactionsTableRef = ref(null);
const reviewingNomination = ref(null);
const togglingProgramId = ref(null);
const togglingProgramName = ref('');
const togglingGroupId = ref(null);
const pendingGroupDisable = ref(null);
const SHOW_DISABLED_AWARDS_STORAGE_KEY = 'sme.recognition.showDisabledAwards';
const storedShowDisabledAwards = localStorage.getItem(SHOW_DISABLED_AWARDS_STORAGE_KEY);
const showDisabledAwards = ref(storedShowDisabledAwards === null ? true : storedShowDisabledAwards === 'true');
watch(showDisabledAwards, value => {
  localStorage.setItem(SHOW_DISABLED_AWARDS_STORAGE_KEY, String(value));
});
const showHiddenActivity = ref(false);
const recognitionPrograms = ref([]);
const {
  selected: selectedAwardScope,
  options: scopeOptions,
  scope: awardScope,
  hasOptions: hasScopeOptions,
} = useScopeFilter();
const awardSearch = ref('');
const filteredPrograms = computed(() => {
  const search = awardSearch.value.trim().toLowerCase();
  return recognitionPrograms.value.filter(
    program =>
      matchesScopeFilter(programScope(program), awardScope.value, { unscopedMatches: true }) &&
      (!search ||
        [program.program_name, program.recognition_award_group_name].some(name =>
          (name || '').toLowerCase().includes(search),
        )),
  );
});
const recognitionLedger = ref({});
const pendingNominations = ref([]);
const upcomingAwards = ref([]);
// Unfiltered by the tab's scope picker: the shortfall banner and tab badge cover the whole company.
const allUpcomingAwards = ref([]);
const upcomingSummary = ref(null);
// The window the fetched awards cover. Defaults to the range the Upcoming tab starts on, which is
// also what the API returns when we send no date_to on mount.
const upcomingWindow = ref({ range: UPCOMING_AWARDS_DEFAULT_RANGE, customDate: null });
const isLoadingLedger = ref(true);
const isLoadingPrograms = ref(true);
const isLoadingPendingNominations = ref(false);
const isLoadingUpcomingAwards = ref(false);
const pendingNominationsLoaded = ref(false);
const upcomingAwardsLoaded = ref(false);
const CONTENT_REPORTS_PAGE_SIZE = 100;
const contentReports = ref([]);
const contentReportsTruncated = ref(false);
const pendingReportsCount = ref(0);
const isLoadingContentReports = ref(false);
const contentReportsLoaded = ref(false);
const reviewingReport = ref(null);
const reviewReportModalRef = ref(null);
// Read-only recognition viewers get the programme list, analytics and activity.
// Everything else on this page is write-gated and would 403.
const isReadOnly = computed(() => isRecognitionViewerClaims(State.state.claims));

const isLoading = computed(() => isLoadingLedger.value || isLoadingPrograms.value);

const currency = computed(() => State.state?.company?.default_currency);

// Withdrawn awards are listed so they can be reinstated, but they won't be given.
const scheduledUpcomingAwards = computed(() => allUpcomingAwards.value.filter(award => !award.withdrawn_at));

const totalUpcomingCostMinor = computed(() => {
  return scheduledUpcomingAwards.value.reduce((sum, award) => sum + (award.amount_minor || 0), 0);
});

const upcomingWindowPhrase = computed(() =>
  getUpcomingWindowPhrase(upcomingWindow.value.range, upcomingWindow.value.customDate),
);

const shortfallMinor = computed(() => {
  const balance = recognitionLedger.value?.balance_minor ?? 0;
  return Math.max(0, totalUpcomingCostMinor.value - balance);
});

const formatShortfall = value => toCurrency(value, currency.value).replace(/\.00$/, '');

const getRecognitionLedger = async () => {
  isLoadingLedger.value = true;
  try {
    const ledger = await ApiClient.getRecognitionLedger();
    recognitionLedger.value = ledger;
  } catch (e) {
    recognitionLedger.value = {};
  } finally {
    isLoadingLedger.value = false;
  }
};

const getRecognitionPrograms = async () => {
  isLoadingPrograms.value = true;
  try {
    const programs = await ApiClient.getRecognitionPrograms({ includeInactive: true });
    recognitionPrograms.value = programs;
  } catch (e) {
    recognitionPrograms.value = [];
  } finally {
    isLoadingPrograms.value = false;
  }
};

const getPendingNominations = async () => {
  isLoadingPendingNominations.value = true;
  try {
    const nominations = await ApiClient.getPendingNominations();
    pendingNominations.value = nominations || [];
    pendingNominationsLoaded.value = true;
  } catch (e) {
    pendingNominations.value = [];
  } finally {
    isLoadingPendingNominations.value = false;
  }
};

const onPendingTabShown = () => {
  // Load pending nominations when tab is first shown
  if (!pendingNominationsLoaded.value) {
    getPendingNominations();
  }
};

const upcomingWindowDateTo = ref(null);
const upcomingScope = ref(null);

const getUpcomingAwards = async () => {
  isLoadingUpcomingAwards.value = true;
  try {
    const dateTo = upcomingWindowDateTo.value;
    const scope = upcomingScope.value;
    const [awards, allAwards] = await Promise.all([
      ApiClient.getUpcomingMilestoneAwards(null, dateTo, scope),
      scope ? ApiClient.getUpcomingMilestoneAwards(null, dateTo) : null,
    ]);
    upcomingAwards.value = awards || [];
    allUpcomingAwards.value = (scope ? allAwards : awards) || [];
    upcomingAwardsLoaded.value = true;
  } catch (e) {
    upcomingAwards.value = [];
    allUpcomingAwards.value = [];
  } finally {
    isLoadingUpcomingAwards.value = false;
  }
};

const onUpcomingTabShown = () => {
  // Load upcoming awards when tab is first shown
  if (!upcomingAwardsLoaded.value) {
    getUpcomingAwards();
  }
};

const getContentReportsCount = async () => {
  try {
    pendingReportsCount.value = await queryClient.fetchQuery(getRecognitionContentReportsCount());
  } catch (e) {
    // Endpoint not deployed yet or transient failure — no badge, never a broken page.
    pendingReportsCount.value = 0;
  }
};

const getContentReports = async () => {
  isLoadingContentReports.value = true;
  try {
    // Fetch one page; the Reported content tab filters client-side
    // (time / status / report count) over this array. The list is hard-capped at
    // CONTENT_REPORTS_PAGE_SIZE and shares that cap across all statuses, so with
    // a large backlog some PENDING items could be pushed past the cap and become
    // unreachable while the danger badge (true server pending total) still counts
    // them. Bounded fix (no full server-side pagination): we don't change the
    // fetch, but we surface an honest reconciliation. The page passes the true
    // pending total (pendingReportsCount) and the number of pending items present
    // in this fetched page to ReportsTable, which tells the admin when pending
    // items are missing from the page rather than silently dropping them.
    contentReports.value = await queryClient.fetchQuery(
      getRecognitionContentReports({ perPage: CONTENT_REPORTS_PAGE_SIZE }),
    );
    contentReportsTruncated.value = contentReports.value.length >= CONTENT_REPORTS_PAGE_SIZE;
    contentReportsLoaded.value = true;
  } catch (e) {
    contentReports.value = [];
    contentReportsTruncated.value = false;
  } finally {
    isLoadingContentReports.value = false;
  }
};

const onReportsTabShown = () => {
  if (!contentReportsLoaded.value) {
    getContentReports();
  }
};

const onReviewReport = report => {
  reviewingReport.value = report;
  // Pass the report into show() — the :report prop hasn't propagated to the
  // child yet in this tick, so the modal can't rely on it for the detail fetch.
  reviewReportModalRef.value?.show(report);
};

const onReportActioned = () => {
  getContentReports();
  getContentReportsCount();
  // A hide/restore/dismiss changes which rows the Activity tab is served, so its
  // cached data is now stale. Invalidate the whole recognition_activity prefix
  // (every param variant) so it refetches the next time that tab is viewed.
  queryClient.invalidateQueries({ queryKey: ['recognition_activity'] });
};

const onUpcomingWindowChange = dateTo => {
  upcomingWindowDateTo.value = dateTo;
  getUpcomingAwards();
};

const onUpcomingScopeChange = scope => {
  upcomingScope.value = scope;
  getUpcomingAwards();
};

const onProgramCreated = () => {
  getRecognitionPrograms();
  // Reset loaded flags so tabs will refetch data for the new program
  pendingNominationsLoaded.value = false;
  upcomingAwardsLoaded.value = false;
};

const onAwardGiven = () => {
  getRecognitionLedger();
  getRecognitionPrograms();
  getPendingNominations();
  getUpcomingAwards();
};

const onGrantWithdrawn = () => {
  getRecognitionLedger();
  transactionsTableRef.value?.refresh();
};

const onReviewNomination = nomination => {
  reviewingNomination.value = nomination;
  reviewModalRef.value?.show();
};

const onNominationReviewed = () => {
  getRecognitionLedger();
  getPendingNominations();
};

const onToggleActive = ({ programId, programName, currentIsActive }) => {
  if (currentIsActive) {
    // Disabling -- show confirmation
    pendingGroupDisable.value = null;
    togglingProgramId.value = programId;
    togglingProgramName.value = programName;
    confirmDisableModalRef.value?.show();
  } else {
    // Enabling -- no confirmation needed
    updateProgramActiveStatus(programId, true);
  }
};

const onToggleGroupActive = group => {
  if (group.isActive) {
    updateGroupActiveStatus(group);
    return;
  }
  pendingGroupDisable.value = group;
  togglingProgramName.value = group.groupName;
  confirmDisableModalRef.value?.show();
};

const updateGroupActiveStatus = async ({ groupId, programIds, isActive }) => {
  togglingGroupId.value = groupId;
  try {
    await ApiClient.bulkUpdateRecognitionAwardGroupMembers(
      groupId,
      programIds.map(id => ({ recognition_award_program_id: id, is_active: isActive })),
    );
    doToast('Success', `Awards ${isActive ? 'enabled' : 'disabled'} successfully`, TOAST_VARIANT.SUCCESS);
    await getRecognitionPrograms();
  } catch (e) {
    const message = apiErrorData(e).message || e?.message || `Failed to ${isActive ? 'enable' : 'disable'} awards`;
    doToast('Error', message, TOAST_VARIANT.DANGER);
  } finally {
    togglingGroupId.value = null;
    pendingGroupDisable.value = null;
    togglingProgramName.value = '';
  }
};

const onDisableConfirmed = () => {
  if (pendingGroupDisable.value) {
    updateGroupActiveStatus(pendingGroupDisable.value);
    return;
  }
  if (togglingProgramId.value) {
    updateProgramActiveStatus(togglingProgramId.value, false);
  }
};

const updateProgramActiveStatus = async (programId, isActive) => {
  togglingProgramId.value = programId;
  try {
    await ApiClient.updateRecognitionProgram(programId, { is_active: isActive });
    doToast('Success', `Award ${isActive ? 'enabled' : 'disabled'} successfully`, TOAST_VARIANT.SUCCESS);
    await getRecognitionPrograms();
  } catch (e) {
    const message = apiErrorData(e).message || e?.message || `Failed to ${isActive ? 'enable' : 'disable'} award`;
    doToast('Error', message, TOAST_VARIANT.DANGER);
  } finally {
    togglingProgramId.value = null;
    togglingProgramName.value = '';
  }
};

onMounted(async () => {
  if (isReadOnly.value) {
    isLoadingLedger.value = false;
    await getRecognitionPrograms();
    return;
  }
  await Promise.all([
    getRecognitionLedger(),
    getRecognitionPrograms(),
    getPendingNominations(),
    getUpcomingAwards(),
    getContentReportsCount(),
  ]);
});
</script>

<style scoped lang="scss">
.header-stat {
  height: auto;
  // Match the width of the "Give recognition" + "Create award" button group
  // below so the card's left edge lines up with the start of "Give recognition".
  width: 301px;

  :deep(.app-stat__value-container) {
    width: 16rem;
    justify-content: flex-start;
  }

  :deep(.app-stat__description) {
    color: var(--palette-color-default-lighten-30);
  }
}

.card-body {
  height: 100%;
  color: var(--palette-color-default);
}

.card-button {
  color: var(--palette-color-default);
  width: fit-content;
}

.recognition-image {
  @media (max-width: 992px) {
    display: none;
    visibility: hidden;
  }

  width: 420px;
  height: 240px;
  object-fit: cover;
  object-position: center;
  display: block;
}

.add-funds-link {
  text-decoration: none;

  &:hover {
    text-decoration: none;
  }
}

.shortfall-alert {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 1rem;
}

.tabs-end-actions {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  justify-content: flex-end;
  gap: 0.25rem 1rem;
  margin-left: auto;

  @media (max-width: 576px) {
    flex-direction: column;
    align-items: flex-start;
    width: 100%;
    margin-left: 0;
    margin-top: 0.5rem;
  }
}

.awards-filters {
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem;
  padding: 1rem;
  margin-bottom: 1rem;
  background: var(--palette-color-default-lighten-95);
  border-radius: 0.5rem;

  :deep(.awards-filters__filter) {
    width: auto;
    min-width: 170px;
    height: auto;
    border-color: var(--palette-color-default-lighten-80);
    padding: 0.5rem 1rem;

    &:focus {
      border-color: var(--palette-color-primary);
      box-shadow: none;
    }
  }

  // Fixed rather than sized to the label, so picking a longer option does not reflow the bar.
  :deep(.awards-filters__scope) {
    width: 12rem;
  }
}

.show-disabled-toggle {
  display: flex;
  align-items: center;
  white-space: nowrap;
  font-size: 0.875rem;

  :deep(input:checked + .custom-control-label) {
    font-weight: 400;
  }
}

.upcoming-summary {
  align-self: center;
  white-space: nowrap;
  font-size: 0.75rem;

  strong {
    font-weight: 600;
    color: var(--palette-color-default);
  }
}

.tab-placeholder {
  padding: 3rem;
  text-align: center;
}
</style>
